import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getProductPrice, getDefaultPlan, formatCurrency, hasPlans, getProductPriceRange, hasMultiplePlans } from '../../utils/productPricing';
import { API_URL } from '../../config/api';
import { 
  useAffiliateDetection, 
  detectAffiliateFromUrl, 
  processAffiliateLink,
  debugAffiliateCookies 
} from '../../utils/cookieUtils';

const PublicCheckout = () => {
  const { productId, checkoutId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Estados do produto e checkout
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados do formulário
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    document: ''
  });
  
  const [shipping, setShipping] = useState({
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: 'Brasil'
  });
  
  const [payment, setPayment] = useState({
    method: 'credit_card', // credit_card, debit_card, boleto, pix, after_pay
    installments: 1,
    cardData: {
      number: '',
      name: '',
      expiry: '',
      cvv: ''
    }
  });
  
  // Estados de processamento
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [saleData, setSaleData] = useState(null);
  
  // Estados de afiliado
  const [affiliateData, setAffiliateData] = useState(null);
  const [affiliateLoading, setAffiliateLoading] = useState(true);
  const { checkForAffiliate, clearAffiliate } = useAffiliateDetection();
  
  // Parâmetros da URL (afiliado)
  const affiliateId = searchParams.get('affiliate_id');
  const affiliateCode = searchParams.get('ref');

  // Carregar dados do produto
  useEffect(() => {
    loadProduct();
  }, [productId]);

  // Detectar afiliado após carregamento do produto
  useEffect(() => {
    if (product && productId) {
      detectAffiliate();
    }
  }, [product, productId, searchParams]);

  const detectAffiliate = async () => {
    try {
      setAffiliateLoading(true);
      
      // 1. Verificar se há parâmetros de afiliado na URL
      const affiliateFromUrl = detectAffiliateFromUrl();
      
      if (affiliateFromUrl && affiliateFromUrl.product_id === parseInt(productId)) {
        console.log('🔗 Processando link de afiliado da URL...');
        
        // Processar link e definir cookie
        await processAffiliateLink(
          affiliateFromUrl.product_id,
          affiliateFromUrl.affiliate_id,
          { linkCode: affiliateFromUrl.link_code }
        );
        
        setAffiliateData(affiliateFromUrl);
        
        // Mostrar notificação discreta
        console.log(`🎯 Compra rastreada para afiliado ${affiliateFromUrl.affiliate_id}`);
      } else {
        // 2. Verificar se já existe cookie de afiliado
        const existingAffiliate = checkForAffiliate(parseInt(productId));
        
        if (existingAffiliate) {
          console.log('🍪 Afiliado encontrado em cookie existente');
          setAffiliateData(existingAffiliate);
        } else {
          console.log('ℹ️ Nenhum afiliado detectado para este produto');
          setAffiliateData(null);
        }
      }
      
      // Debug para desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        debugAffiliateCookies();
      }
      
    } catch (error) {
      console.error('🔗 Erro ao detectar afiliado:', error);
      setAffiliateData(null);
    } finally {
      setAffiliateLoading(false);
    }
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/store/products/${productId}`);
      
      if (!response.ok) {
        throw new Error('Produto não encontrado');
      }
      
      const data = await response.json();
      setProduct(data.product);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Buscar endereço por CEP
  const fetchAddressByCep = async (cep) => {
    try {
      const cleanCep = cep.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;
      
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setShipping(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || ''
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  // Processar compra
  const processPurchase = async () => {
    try {
      setProcessing(true);

      // Validações básicas
      if (!customer.name || !customer.email || !customer.phone) {
        throw new Error('Preencha todos os dados pessoais');
      }

      if (!shipping.zipCode || !shipping.street || !shipping.number) {
        throw new Error('Preencha os dados de entrega');
      }

      if (payment.method === 'credit_card' && (!payment.cardData.number || !payment.cardData.name)) {
        throw new Error('Preencha os dados do cartão');
      }

      // Criar venda
      const saleData = {
        product_id: parseInt(productId),
        plan_id: product?.plans?.[0]?.id || null,
        customer_data: customer,
        affiliate_id: affiliateData?.affiliate_id || null, // Usar dados do cookie/URL
        payment_method: payment.method,
        total_amount: getProductPrice(product),
        shipping_address: {
          ...shipping,
          recipient: customer.name
        },
        payment_data: payment.method === 'credit_card' ? {
          installments: payment.installments,
          card_hash: 'mock_card_hash' // Em produção seria o hash real
        } : null
      };

      const response = await fetch(`${API_URL}/api/checkout/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao processar compra');
      }

      const result = await response.json();
      setSaleData(result.data);
      setCompleted(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Componente de loading
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted">Carregando produto...</p>
        </div>
      </div>
    );
  }

  // Componente de erro
  if (error && !product) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Icon icon="solar:close-circle-bold" className="text-danger mb-3" style={{ fontSize: '64px' }} />
          <h3 className="text-danger mb-3">Produto não encontrado</h3>
          <p className="text-muted mb-4">{error}</p>
          <button 
            onClick={() => window.history.back()} 
            className="btn btn-primary"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Tela de sucesso
  if (completed && saleData) {
    return (
      <div className="min-vh-100 bg-light">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card shadow-sm border-0">
                <div className="card-body text-center p-5">
                  <Icon icon="solar:check-circle-bold" className="text-success mb-4" style={{ fontSize: '80px' }} />
                  
                  <h2 className="text-success mb-3">Compra Realizada!</h2>
                  <p className="text-muted mb-4">Seu pedido foi processado com sucesso.</p>
                  
                  <div className="bg-light rounded p-4 mb-4">
                    <div className="row text-start">
                      <div className="col-6">
                        <small className="text-muted">Pedido</small>
                        <p className="fw-bold mb-2">#{saleData.id}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Total</small>
                        <p className="fw-bold mb-2">R$ {parseFloat(saleData.total_amount).toFixed(2)}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Pagamento</small>
                        <p className="fw-bold mb-2">
                          {payment.method === 'credit_card' && 'Cartão de Crédito'}
                          {payment.method === 'debit_card' && 'Cartão de Débito'}
                          {payment.method === 'boleto' && 'Boleto'}
                          {payment.method === 'pix' && 'PIX'}
                          {payment.method === 'after_pay' && 'After Pay'}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Status</small>
                        <p className="fw-bold mb-2">
                          <span className="badge bg-warning">Processando</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {payment.method === 'boleto' && (
                    <div className="alert alert-info mb-4">
                      <Icon icon="solar:document-text-bold" className="me-2" />
                      O boleto será enviado para seu email em até 5 minutos.
                    </div>
                  )}

                  {payment.method === 'pix' && (
                    <div className="alert alert-info mb-4">
                      <Icon icon="solar:qr-code-bold" className="me-2" />
                      O código PIX será enviado para seu email.
                    </div>
                  )}

                  <div className="d-grid gap-2">
                    <button 
                      onClick={() => navigate('/')} 
                      className="btn btn-primary"
                    >
                      Continuar Comprando
                    </button>
                    <button 
                      onClick={() => window.print()} 
                      className="btn btn-outline-secondary"
                    >
                      <Icon icon="solar:printer-bold" className="me-2" />
                      Imprimir Comprovante
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-white border-bottom">
        <div className="container py-3">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="mb-0">
                <Icon icon="solar:cart-4-bold" className="me-2 text-primary" />
                Finalizar Compra
              </h4>
            </div>
            <div className="col-auto">
              <button 
                onClick={() => window.history.back()}
                className="btn btn-outline-secondary btn-sm"
              >
                <Icon icon="solar:arrow-left-bold" className="me-1" />
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        <div className="row">
          {/* Resumo do Produto */}
          <div className="col-lg-4 order-lg-2 mb-4">
            <div className="card shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
              <div className="card-body">
                <h5 className="card-title mb-3">Resumo do Pedido</h5>
                
                {product && (
                  <div className="d-flex mb-3">
                    <div className="flex-shrink-0 me-3">
                      <img 
                        src={product.image || product.image_url || "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg"} 
                        alt={product.name}
                        className="rounded"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg";
                        }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{product.name}</h6>
                      <small className="text-muted">Código: {product.code}</small>
                      <div className="mt-2">
                        <span className="badge bg-primary">{product.category}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Indicador de Afiliado */}
                {affiliateData && !affiliateLoading && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                    <Icon icon="mdi:account-star" className="me-2" />
                    <strong>Compra via indicação!</strong> Esta venda será creditada ao seu indicador.
                    <button 
                      type="button" 
                      className="btn-close btn-close-sm" 
                      onClick={() => setAffiliateData(null)}
                      style={{ fontSize: '0.75rem' }}
                    ></button>
                  </div>
                )}

                <hr />

                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(getProductPrice(product))}</span>
                </div>
                
                <div className="d-flex justify-content-between mb-2">
                  <span>Frete:</span>
                  <span className="text-success">Grátis</span>
                </div>

                {affiliateId && (
                  <div className="d-flex justify-content-between mb-2 text-success">
                    <span>
                      <Icon icon="solar:user-heart-bold" className="me-1" />
                      Indicação:
                    </span>
                    <span>Aplicada</span>
                  </div>
                )}

                <hr />

                <div className="d-flex justify-content-between fw-bold">
                  <span>Total:</span>
                  <span className="text-primary">{formatCurrency(getProductPrice(product))}</span>
                </div>

                {product?.has_after_pay && (
                  <div className="mt-3 p-2 bg-success-subtle rounded">
                    <small className="text-success">
                      <Icon icon="solar:dollar-circle-bold" className="me-1" />
                      After Pay disponível
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Formulário de Checkout */}
          <div className="col-lg-8 order-lg-1">
            <form onSubmit={(e) => { e.preventDefault(); processPurchase(); }}>
              {/* Dados Pessoais */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <Icon icon="solar:user-bold" className="me-2 text-primary" />
                    Dados Pessoais
                  </h5>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Nome Completo *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={customer.name}
                        onChange={(e) => setCustomer(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={customer.email}
                        onChange={(e) => setCustomer(prev => ({ ...prev, email: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Telefone *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={customer.phone}
                        onChange={(e) => setCustomer(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(11) 99999-9999"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">CPF/CNPJ</label>
                      <input
                        type="text"
                        className="form-control"
                        value={customer.document}
                        onChange={(e) => setCustomer(prev => ({ ...prev, document: e.target.value }))}
                        placeholder="000.000.000-00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Endereço de Entrega */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <Icon icon="solar:map-point-bold" className="me-2 text-primary" />
                    Endereço de Entrega
                  </h5>
                  
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">CEP *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.zipCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          setShipping(prev => ({ ...prev, zipCode: value }));
                          if (value.replace(/\D/g, '').length === 8) {
                            fetchAddressByCep(value);
                          }
                        }}
                        placeholder="00000-000"
                        required
                      />
                    </div>
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Rua *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.street}
                        onChange={(e) => setShipping(prev => ({ ...prev, street: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Número *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.number}
                        onChange={(e) => setShipping(prev => ({ ...prev, number: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Complemento</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.complement}
                        onChange={(e) => setShipping(prev => ({ ...prev, complement: e.target.value }))}
                        placeholder="Apt, Bloco, etc"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bairro *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.neighborhood}
                        onChange={(e) => setShipping(prev => ({ ...prev, neighborhood: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Cidade *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={shipping.city}
                        onChange={(e) => setShipping(prev => ({ ...prev, city: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Estado *</label>
                      <select
                        className="form-select"
                        value={shipping.state}
                        onChange={(e) => setShipping(prev => ({ ...prev, state: e.target.value }))}
                        required
                      >
                        <option value="">Selecione...</option>
                        <option value="SP">São Paulo</option>
                        <option value="RJ">Rio de Janeiro</option>
                        <option value="MG">Minas Gerais</option>
                        <option value="RS">Rio Grande do Sul</option>
                        <option value="PR">Paraná</option>
                        <option value="SC">Santa Catarina</option>
                        {/* Adicionar outros estados conforme necessário */}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Forma de Pagamento */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <Icon icon="solar:card-bold" className="me-2 text-primary" />
                    Forma de Pagamento
                  </h5>
                  
                  {/* Método de Pagamento */}
                  <div className="row mb-4">
                    {[
                      { value: 'credit_card', icon: 'solar:card-bold', label: 'Cartão de Crédito', desc: 'Até 12x sem juros' },
                      { value: 'debit_card', icon: 'solar:card-2-bold', label: 'Cartão de Débito', desc: 'À vista' },
                      { value: 'pix', icon: 'solar:qr-code-bold', label: 'PIX', desc: 'Aprovação imediata' },
                      { value: 'boleto', icon: 'solar:document-text-bold', label: 'Boleto', desc: 'Vence em 3 dias' },
                      ...(product?.has_after_pay ? [{ value: 'after_pay', icon: 'solar:dollar-circle-bold', label: 'After Pay', desc: 'Compre agora, pague depois' }] : [])
                    ].map((method) => (
                      <div key={method.value} className="col-md-6 col-lg-4 mb-3">
                        <div 
                          className={`card h-100 cursor-pointer ${payment.method === method.value ? 'border-primary' : ''}`}
                          onClick={() => setPayment(prev => ({ ...prev, method: method.value }))}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center p-3">
                            <Icon icon={method.icon} className={`mb-2 ${payment.method === method.value ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '24px' }} />
                            <h6 className="card-title mb-1">{method.label}</h6>
                            <small className="text-muted">{method.desc}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Dados do Cartão */}
                  {(payment.method === 'credit_card' || payment.method === 'debit_card') && (
                    <div className="mt-4">
                      <h6 className="mb-3">Dados do Cartão</h6>
                      <div className="row">
                        <div className="col-md-8 mb-3">
                          <label className="form-label">Número do Cartão *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={payment.cardData.number}
                            onChange={(e) => setPayment(prev => ({ 
                              ...prev, 
                              cardData: { ...prev.cardData, number: e.target.value }
                            }))}
                            placeholder="0000 0000 0000 0000"
                            required
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">CVV *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={payment.cardData.cvv}
                            onChange={(e) => setPayment(prev => ({ 
                              ...prev, 
                              cardData: { ...prev.cardData, cvv: e.target.value }
                            }))}
                            placeholder="000"
                            maxLength="4"
                            required
                          />
                        </div>
                        <div className="col-md-8 mb-3">
                          <label className="form-label">Nome no Cartão *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={payment.cardData.name}
                            onChange={(e) => setPayment(prev => ({ 
                              ...prev, 
                              cardData: { ...prev.cardData, name: e.target.value }
                            }))}
                            placeholder="Nome conforme impresso no cartão"
                            required
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Validade *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={payment.cardData.expiry}
                            onChange={(e) => setPayment(prev => ({ 
                              ...prev, 
                              cardData: { ...prev.cardData, expiry: e.target.value }
                            }))}
                            placeholder="MM/AA"
                            required
                          />
                        </div>
                      </div>

                      {payment.method === 'credit_card' && (
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label">Parcelas</label>
                            <select
                              className="form-select"
                              value={payment.installments}
                              onChange={(e) => setPayment(prev => ({ ...prev, installments: parseInt(e.target.value) }))}
                            >
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(i => (
                                <option key={i} value={i}>
                                  {i}x de {formatCurrency(getProductPrice(product) / i)} {i === 1 ? 'à vista' : 'sem juros'}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Erro */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  <Icon icon="solar:close-circle-bold" className="me-2" />
                  {error}
                </div>
              )}

              {/* Botão de Finalizar */}
              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={processing || !product}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:check-circle-bold" className="me-2" />
                      Finalizar Compra - {formatCurrency(getProductPrice(product))}
                    </>
                  )}
                </button>
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  <Icon icon="solar:shield-check-bold" className="me-1" />
                  Seus dados estão protegidos com segurança SSL
                </small>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCheckout;